'use client';

import cx from 'classnames';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { Alert } from '../../../../../../design-system/components/alert/Alert';
import { Anchor } from '../../../../../../design-system/components/anchor/Anchor';
import { Breadcrumb } from '../../../../../../design-system/components/breadcrumb/Breadcrumb';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { IconQuote } from '../../../../../../design-system/components/icons/custom/Quote';
import { Ratio, RATIO } from '../../../../../../design-system/components/ratio/Ratio';
import { AgencyTag } from '../../../../../../design-system/components/tag/agency-tag/AgencyTag';
import { useAgencyBreadcrumbItems } from '../../../../../../domains/Layout/Breadcrumb/Breadcrumb';
import { HtmlContent } from '../../../../../../domains/Layout/HtmlContent/HtmlContent';
import { imageLoaderForId } from '../../../../../../domains/Layout/Image/URL/ImageURL';
import { Agency } from '../../../../../../resources/agency/domain/types/Agency';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { ReviewRating } from '../../../../../../shared/components/ReviewRating/ReviewRating';
import { useLocale } from '../../../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { getAgencyYearsOfService } from '../../../../../../shared/utilities/getAgencyYearsOfService';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';
import { getIconByCertification } from '../shared/utilities/getIconByCertification';

import style from './AgencyHeader.module.css';

type AgencyHeaderProps = {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
    agency: PickDomain<
        Agency,
        | 'name'
        | 'description'
        | 'id'
        | 'slug'
        | 'picture'
        | 'tripReviewsAverageRating'
        | 'evaneosMemberSince'
        | 'creationDate'
        | 'spokenLanguages'
        | 'certifications'
        | 'areasOfExpertise'
        | 'isLocalHero'
        | 'longDescription'
        | 'betterTripIndex'
        | 'isAvailable'
        | 'nameTranslationRule',
        {
            workplace: {
                name: Agency['workplace']['name'];
                destinationName: Agency['workplace']['destinationName'] | null;
            } | null;
        }
    >;
};

export const AgencyHeader = ({ destination, agency }: AgencyHeaderProps) => {
    const {
        name,
        description,
        id,
        slug,
        picture,
        tripReviewsAverageRating,
        evaneosMemberSince,
        creationDate,
        spokenLanguages,
        certifications,
        areasOfExpertise,
        workplace,
    } = agency;

    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();
    const { locale } = useLocale();
    const [showAlert, setShowAlert] = useState(false);

    const yearMemberOfEvaneos = getAgencyYearsOfService(new Date(evaneosMemberSince));
    const yearOfExperience = getAgencyYearsOfService(new Date(creationDate));

    const { breadcrumbItems } = useAgencyBreadcrumbItems({
        destination,
        agencyName: name,
        agencyDescription: description,
        agencyId: id,
        agencySlug: slug,
    });

    const link = generatePath('agency', {
        destinationSlug: destination.slug,
        agencyId: id,
        agencySlug: slug,
    });

    const agencyTitle = formatMessage(
        { id: 'global.component.agencyCard.name' },
        { name: agency.name, nameTranslationRule: agency.nameTranslationRule },
    );

    const details = useMemo(
        () => [
            {
                label: formatMessage({ id: 'page.agency.member' }),
                value: formatMessage(
                    { id: 'page.agency.member-since-date' },
                    {
                        memberSinceDate: yearMemberOfEvaneos,
                    },
                ),
            },
            {
                label: formatMessage({ id: 'page.agency.experience' }),
                value: formatMessage(
                    { id: 'page.agency.experience-since-date' },
                    {
                        experienceSinceDate: yearOfExperience,
                    },
                ),
            },
            ...(spokenLanguages.length
                ? [
                      {
                          label: formatMessage({ id: 'page.agency.languages' }),
                          value: spokenLanguages
                              .map((language) =>
                                  formatMessage({
                                      id: `page.agency.language-${language}` as MessageDescriptor['id'],
                                  }),
                              )
                              .join(', '),
                      },
                  ]
                : []),
            ...(workplace
                ? [
                      {
                          label: formatMessage({ id: 'global.agency.based' }),
                          value: `${workplace.name}, ${workplace.destinationName}`,
                      },
                  ]
                : []),

            ...(certifications.length
                ? [
                      {
                          label: certifications.some(
                              (certification) => certification.level === 'Qualified',
                          )
                              ? formatMessage(
                                    { id: 'page.agency.certifications-in-progress' },
                                    { certificationsCount: certifications.length },
                                )
                              : formatMessage(
                                    { id: 'page.agency.certifications' },
                                    { certificationsCount: certifications.length },
                                ),
                          value: certifications
                              .map((certification) => certification.name)
                              .join(', '),
                      },
                  ]
                : []),
        ],
        [
            certifications,
            formatMessage,
            spokenLanguages,
            workplace,
            yearMemberOfEvaneos,
            yearOfExperience,
        ],
    );

    return (
        <header className={style.section}>
            <div className={style.breadcrumbWrapper}>
                <Breadcrumb reversed items={breadcrumbItems} />
            </div>
            <div className={style.agencySection}>
                <div className={style.agencyPictureContainer}>
                    {agency.isLocalHero ? (
                        <>
                            <p className={cx(style.local, style.localHeroText)}>
                                {
                                    formatMessage({
                                        id: 'global.component.localHeroBadge.content',
                                    }).split(' ')[0]
                                }
                            </p>
                            <p className={cx(style.hero, style.localHeroText)}>
                                {
                                    formatMessage({
                                        id: 'global.component.localHeroBadge.content',
                                    }).split(' ')[1]
                                }
                            </p>
                        </>
                    ) : null}
                    <Ratio ratio={RATIO['3/4']}>
                        <Image
                            alt={agencyTitle}
                            src={picture.id.toString()}
                            loader={imageLoaderForId({ aspectRatio: RATIO['3/4'] })}
                            fill
                            sizes="330px"
                            priority={true}
                            loading="eager"
                            className={style.agencyPicture}
                        />
                    </Ratio>
                </div>
                <div>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <h1 className={cx('font-secondary-xl-bold', style.title)}>{agencyTitle}</h1>
                    <div className={style.agencyTagContainer}>
                        {areasOfExpertise.map((area, index) => (
                            <AgencyTag key={index} area={area} reversed />
                        ))}
                    </div>
                    {tripReviewsAverageRating ? (
                        <div className={style.ratingWrapper}>
                            <ReviewRating
                                inverse={true}
                                rating={tripReviewsAverageRating.rating}
                                reviewsCount={tripReviewsAverageRating.count}
                                obfuscate
                                onClick={() =>
                                    trackEvent((ampli, defaultProperties) =>
                                        ampli.agencyReviewClicked({
                                            ...defaultProperties,
                                            agency_id: agency.id,
                                            agency_slug: agency.slug,
                                            agency_better_trip_index: agency.betterTripIndex,
                                        }),
                                    )
                                }
                                url={`${link}#reviews`}
                            />
                        </div>
                    ) : null}
                    <div className={style.detailsSection}>
                        {details.map((detail, index) => (
                            <div className={style.details} key={index}>
                                {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                                <span className="font-body-bold">{detail.label}</span>
                                <span className={style.detailValue}>{detail.value}</span>
                            </div>
                        ))}
                    </div>
                    {getIconByCertification(agency.certifications, locale).length ? (
                        <div className={cx(style.icons)}>
                            {getIconByCertification(agency.certifications, locale)}
                        </div>
                    ) : null}
                    <div className={style.buttonWrapper}>
                        <div
                            onMouseEnter={() => setShowAlert(true)}
                            onMouseLeave={() => setShowAlert(false)}
                        >
                            <Button
                                disabled={!agency.isAvailable}
                                obfuscate
                                href={generatePath('trip_form', {
                                    destinationSlug: destination.slug,
                                    agency: agency.id,
                                })}
                                onClick={() => {
                                    trackEvent((ampli, defaultProperties) => {
                                        ampli.tripformCreationStartedFromAgency({
                                            ...defaultProperties,
                                            agency_id: agency.id,
                                            agency_slug: agency.slug,
                                            agency_better_trip_index: agency.betterTripIndex,
                                            block: 'agent_information',
                                        });
                                    });
                                }}
                            >
                                {formatMessage({ id: 'page.agency.header-cta' })}
                            </Button>
                        </div>
                        <div className={style.alertWrapper}>
                            {!agency.isAvailable && showAlert ? (
                                <Alert
                                    className={style.alert}
                                    type="information"
                                    message={
                                        <div className={style.agencyAvailable}>
                                            {formatMessage(
                                                {
                                                    id: 'page.itinerary.unavailableItineraryMessage',
                                                },
                                                {
                                                    agencyName: agency.name,
                                                    destinationName: destination.name,
                                                    destinationNamePreposition:
                                                        destination.preposition,
                                                    itinerariesLink: (chunks) => (
                                                        <Anchor
                                                            key={`itineraries-link-${String(chunks)}`}
                                                            href={generatePath('itinerary_list', {
                                                                destinationSlug: destination.slug,
                                                            })}
                                                        >
                                                            {chunks}
                                                        </Anchor>
                                                    ),
                                                    agenciesLink: (chunks) => (
                                                        <Anchor
                                                            key={`agencies-link-${String(chunks)}`}
                                                            href={generatePath('agency_list', {
                                                                destinationSlug: destination.slug,
                                                            })}
                                                        >
                                                            {chunks}
                                                        </Anchor>
                                                    ),
                                                    agencyNameTranslationRule:
                                                        agency.nameTranslationRule,
                                                },
                                            )}
                                        </div>
                                    }
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
            <div className={style.wordOfSection}>
                <header className={style.wordOfHeader}>
                    <IconQuote width="50px" className={style.iconQuote} />
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <h3 className="font-m-extrabold">
                        {formatMessage(
                            { id: 'page.agency.word-of-agency-name' },

                            { agencyName: name },
                        )}
                    </h3>
                </header>
                <HtmlContent htmlContent={agency.longDescription} />
            </div>
        </header>
    );
};
